import {
  Blockchain,
  ConnectWalletError,
  getMainChainId,
  isConnectWalletError,
  registerWallet,
  translations,
} from './common'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AxiosInstance } from 'axios'
import {
  useAccount,
  useAccountEffect,
  UseAccountReturnType,
  useConnect,
  useConnectors,
  useDisconnect,
  useSwitchChain,
} from 'wagmi'
import { ClaimPlatform } from './http'
import { useQueryClient } from '@tanstack/react-query'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import {
  Account,
  Chain,
  createWalletClient,
  custom,
  Transport,
  WalletClient,
} from 'viem'
import { mainnet } from 'viem/chains'

/**
 * Hook used for linking the current wallet to core.
 */
export const useLinkWallet = (
  api?: AxiosInstance,
  setError?: (e: ConnectWalletError | string | undefined) => void
) => {
  const { ensureBlockchain, disconnect } = useConnectWallet()

  const [isLinked, setIsLinked] = useState(false)

  const queryClient = useQueryClient()

  const linkWallet = useCallback(
    async (
      claimPlatform: ClaimPlatform,
      walletClient: WalletClient<Transport, Chain, Account>,
      shopDomain?: string,
      /** The initial blockchain to connect to if known */
      blockchain?: Blockchain,
      force = false
    ) => {
      try {
        if (!api) {
          return
        }

        if (blockchain) {
          await ensureBlockchain(blockchain)
        }

        await registerWallet(
          api,
          walletClient,
          force,
          claimPlatform,
          shopDomain
        )

        // Make sure the form will immediately reflect the linked change
        await queryClient.invalidateQueries({ queryKey: ['account'] })

        setError?.(undefined)
        setIsLinked(true)
      } catch (e: any) {
        if (e.cause !== 'already_linked') {
          console.error('Error in linkWallet', e)
          if (isConnectWalletError(e)) {
            if (
              e.cause === 'metamask_user_cancelled' ||
              e.cause === 'magic_user_cancelled'
            ) {
              await disconnect()
            }
            setError?.(
              e.cause === 'already_claimed_by_other'
                ? e
                : 'Sorry, your wallet could not be linked. Please try again.'
            )
            return
          } else if (e.status === 401) {
            // This is a special case where the user is not logged in.
            // We don't want to show an error in this case.
            return
          } else {
            await disconnect()
            setError?.(translations['unknown'] + e.message)
            return
          }
        } else {
          setIsLinked(true)
        }

        setError?.(undefined)
      }
    },
    [api, queryClient, setError, ensureBlockchain, disconnect]
  )

  return {
    linkWallet,
    isLinked,
  }
}

export type Compute<type> = { [key in keyof type]: type[key] } & unknown

export type AccountEffectParameters = Compute<{
  onConnect?(
    data: Compute<
      Pick<
        Extract<UseAccountReturnType, { status: 'connected' }>,
        'address' | 'addresses' | 'chain' | 'chainId' | 'connector'
      > & {
        isReconnected: boolean
        walletClient: WalletClient<Transport, Chain, Account>
      }
    >
  ): void
  onDisconnect?(): void
}>

export const useConnectWallet = (parameters?: AccountEffectParameters) => {
  const { openConnectModal, connectModalOpen } = useConnectModal()
  const { address, connector } = useAccount()
  const [connectorIcon, setConnectorIcon] = useState<string>()
  const connectors = useConnectors()
  const { connectAsync } = useConnect()
  const { switchChainAsync } = useSwitchChain()
  const { disconnectAsync: disconnect } = useDisconnect()
  const [magicWalletConnecting, setMagicWalletConnecting] = useState(false)

  useEffect(() => {
    if (connector) {
      if (connector.icon) {
        setConnectorIcon(connector.icon)
      }

      if ((connector as any).rkDetails) {
        if (typeof (connector as any).rkDetails.iconUrl === 'function') {
          ;(connector as any).rkDetails.iconUrl().then(setConnectorIcon)
        } else {
          setConnectorIcon((connector as any).rkDetails.iconUrl)
        }
      }
    }
  }, [connector])

  const showMagic = useMemo(() => {
    if (connector?.id === 'magic') {
      return async () => {
        await (connector as any)?.magic?.wallet?.showUI()
      }
    }
    return undefined
  }, [connector])

  useAccountEffect({
    ...parameters,
    onConnect: async (data) => {
      const { address, chain, connector } = data
      const provider = await connector.getProvider()

      const walletClient = createWalletClient({
        account: address,
        chain: chain || mainnet,
        transport: custom(provider as any),
      })

      parameters?.onConnect?.({
        ...data,
        walletClient,
      })
    },
  })

  return {
    connect: useCallback(async () => {
      openConnectModal?.()
    }, [openConnectModal]),
    connectMagic: useCallback(async () => {
      const magicWallet = connectors.find(
        (connector) => connector.id === 'magic'
      )

      if (magicWallet) {
        setMagicWalletConnecting(true)
        return await connectAsync(
          { connector: magicWallet },
          { onSuccess: () => setMagicWalletConnecting(false) }
        )
      }
    }, [connectors, connectAsync]),
    disconnect,
    ensureBlockchain: useCallback(
      async (blockchain: Blockchain) => {
        if (address) {
          await switchChainAsync({
            connector,
            chainId: getMainChainId(blockchain),
          })
        }
      },
      [switchChainAsync, connector, address]
    ),
    showMagic,
    connecting: (connectModalOpen && !address) || magicWalletConnecting,
    icon: connectorIcon,
  }
}
